import { ChangeDetectionStrategy, Component, computed, inject, input } from '@angular/core';
import { CommonModule } from '@angular/common';

import { DigitsGroupingPipe } from '../pipes';
import { LightCarouselComponent } from '@light-carousel';
import { ProductCart, usdToPoints } from '@models';
import { AlertMessageComponent } from '..';
import {
  ConfigurationService,
  EngravingModalService,
  EngravingUtilitiesService,
  ProcessedProduct,
  ProductProcessingService,
  type DisabledEngravingModalData,
} from '@core';
import { CartOldPriceComponent } from '../cart-old-price/cart-old-price.component';

@Component({
  selector: 'lib-cart-summary-item',
  templateUrl: './cart-summary-item.component.html',
  styleUrl: './cart-summary-item.component.scss',
  changeDetection: ChangeDetectionStrategy.OnPush,
  imports: [CommonModule, LightCarouselComponent, DigitsGroupingPipe, AlertMessageComponent, CartOldPriceComponent],
})
export class CartSummaryItemComponent {
  protected readonly rewardName = inject(ConfigurationService).rewardName;
  product = input<ProductCart | ProcessedProduct>();

  private engravingService = inject(EngravingUtilitiesService);
  private processingService = inject(ProductProcessingService);
  private engravingModalService = inject(EngravingModalService);

  vm = computed(() => {
    const product = this.product();
    const price = product?.price ?? 0;
    const discountedPrice = product?.discountedPrice ?? 0;

    const pricePoints = usdToPoints(price);
    const discountedPricePoints = discountedPrice ? usdToPoints(discountedPrice) : 0;

    const isPromo = product?.isPromo;

    let displayNewPoints: number;
    let displayOldPoints: number | null;
    let displayNewPrice: number;
    let displayOldPrice: number | null;

    if (isPromo && discountedPrice) {
      // Price discount: show discounted price/points, old regular price/points
      displayNewPoints = discountedPricePoints;
      displayNewPrice = discountedPrice;
      displayOldPrice = price;
      displayOldPoints = pricePoints;
    } else {
      // No price discount
      displayNewPoints = pricePoints;
      displayNewPrice = price;
      displayOldPrice = null;
      displayOldPoints = null;
    }

    return {
      product: product,
      smallImagesForCards: product?.images,
      price: price,
      points: pricePoints,
      discountedPrice: discountedPrice,
      discountedPoints: discountedPricePoints,
      // New display values
      displayNewPoints,
      displayOldPoints,
      displayNewPrice,
      displayOldPrice,
      shouldShowOldPoints: displayOldPoints !== null,
    };
  });

  hasEngraving(product: ProductCart | ProcessedProduct): boolean {
    if (this.isProcessedProduct(product)) {
      return this.engravingService.hasEngraving(product);
    }
    return false;
  }
  isProcessedProduct(product: ProductCart | ProcessedProduct): product is ProcessedProduct {
    return 'engravingText' in product && 'isEngravable' in product;
  }
  viewEngravingDetails(product: ProductCart | ProcessedProduct): void {
    if (!this.isProcessedProduct(product)) {
      console.warn('Cannot view engraving details for unprocessed product:', product.productId);
      return;
    }
    if (!product.engravingText) {
      console.warn('No engraving text found for product:', product.productId);
      return;
    }

    const disabledModalData: DisabledEngravingModalData = {
      product: product,
      engravingText: product.engravingText,
    };

    this.engravingModalService.openDisabledModal(disabledModalData);
  }
}
